import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSubmissionDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState(null);
  const [internalNotes, setInternalNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Jigsaw Deal Room – Submission Review";
    init();
  }, [id]);

  const init = async () => {
    try {
      // Check auth
      const authResponse = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      if (authResponse.data.ok) {
        if (authResponse.data.user.role !== "admin") {
          navigate("/deal");
          return;
        }
        setUser(authResponse.data.user);
      }

      // Load submission
      await loadSubmission();
    } catch (err) {
      console.error("Initialization failed:", err);
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  const loadSubmission = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions/${id}`, {
        withCredentials: true,
      });

      if (response.data.ok) {
        setSubmission(response.data.submission);
        setInternalNotes(response.data.submission.internal_notes || "");
      }
    } catch (err) {
      console.error("Failed to load submission:", err);
      if (err.response?.status === 403 || err.response?.status === 404) {
        navigate("/admin");
      }
    }
  };

  const handleDecision = async (decision) => {
    const confirmMsg = decision === "serious" 
      ? "Flag this submission as SERIOUS?"
      : decision === "tourist"
      ? "Flag this submission as TOURIST?"
      : "Mark this submission as REVIEWED?";

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/admin/submissions/${id}/decision`,
        {
          status: "reviewed",
          decision: decision !== "reviewed" ? decision : null,
          internal_notes: internalNotes
        },
        { withCredentials: true }
      );

      if (response.data.ok) {
        alert(`✅ Decision recorded: ${decision.toUpperCase()}`);
        await loadSubmission();
      }
    } catch (err) {
      console.error("Decision error:", err);
      alert("❌ Failed to record decision. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const getScoreBadgeColor = (band) => {
    switch (band) {
      case "serious":
        return "bg-green-500 text-white";
      case "borderline":
        return "bg-yellow-500 text-white";
      case "tourist":
        return "bg-red-500 text-white";
      default:
        return "bg-slate-500 text-white";
    }
  };

  const formatSlot = (slot) => {
    if (!slot) return "Not selected";
    try {
      return new Date(slot).toLocaleString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/London"
      }) + " (UK time)";
    } catch {
      return slot;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white text-lg">Submission not found</div>
      </div>
    );
  }

  const wizard = submission.wizard || {};
  const structure = wizard.structure || {};

  return (
    <div className="min-h-screen text-white flex flex-col relative">
      {/* Background Pattern */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/z6ik3546_Subtle%20Pattern%20On%20Blue.png)',
        }}
      />
      
      {/* Side Fade Overlays */}
      <div className="fixed inset-0 bg-gradient-to-r from-slate-900/60 via-transparent to-slate-900/60 pointer-events-none" />
      
      {/* Header */}
      <header className="relative z-10 border-b border-white/10 bg-slate-900/30 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin")}
                className="text-slate-400 hover:text-white transition"
              >
                ← Back
              </button>
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.peakassetinvestments.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/ff1ei5kf_RGB_Motif%20Small%20Full%20Colour%20White.png"
                    alt="Peak Asset Investments"
                    className="h-8 w-auto"
                  />
                </a>
                <div className="h-6 w-px bg-white/20 mx-2" />
                <div>
                  <div className="text-sm font-semibold">
                    Jigsaw Conferences Ltd – Deal Room (Admin)
                  </div>
                  <div className="text-xs text-slate-400">Submission Review</div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-xs text-slate-400">{user?.email}</div>
              <button
                onClick={handleLogout}
                className="text-xs text-slate-300 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        {/* Header Section */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">{submission.buyer_email}</h1>
              <div className="flex items-center gap-3">
                <span className={`px-4 py-2 rounded-xl text-sm font-bold ${getScoreBadgeColor(submission.score_band)}`}>
                  {submission.score}/100 — {submission.score_band.toUpperCase()}
                </span>
                <span className="px-3 py-1 rounded-lg text-xs bg-blue-500/20 text-blue-300 border border-blue-500/40">
                  {submission.status}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-slate-400 mb-1">Selected Meeting Slot</div>
              <div className="text-white font-medium">{formatSlot(wizard.slot)}</div>
            </div>
            <div>
              <div className="text-slate-400 mb-1">Submitted</div>
              <div className="text-white font-medium">
                {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : "-"}
              </div>
            </div>
          </div>
        </div>

        {/* Summary Panel */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 mb-6">
          <h2 className="text-xl font-bold mb-6">Deal Structure Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-slate-400 mb-1">Transaction Perimeter</div>
              <div className="text-white font-medium">{wizard.perimeter || "-"}</div>
            </div>
            <div>
              <div className="text-sm text-slate-400 mb-1">Structure</div>
              <div className="text-white font-medium">
                Cash {structure.cash_pct || 0}% / VLN {structure.vln_pct || 0}% / Earn-out {structure.earnout_pct || 0}% / Rollover {structure.rollover_pct || 0}%
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-400 mb-1">Timeline to HOTs</div>
              <div className="text-white font-medium">{structure.timeline_to_HOTs || "-"}</div>
            </div>
            <div>
              <div className="text-sm text-slate-400 mb-1">Timeline to Completion</div>
              <div className="text-white font-medium">{structure.timeline_to_completion || "-"}</div>
            </div>
          </div>
        </div>

        {/* Detailed Answers */}
        <div className="space-y-4 mb-6">
          {/* Step 1 */}
          <details className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl overflow-hidden">
            <summary className="px-6 py-4 cursor-pointer hover:bg-white/5 transition font-semibold">
              Investor Roster & Decision Rights
            </summary>
            <div className="px-6 py-4 border-t border-white/10 space-y-4">
              <div>
                <div className="text-sm text-slate-400 mb-2">Investor Roster</div>
                <div className="text-white whitespace-pre-wrap">{wizard.investor_roster || "-"}</div>
              </div>
              <div>
                <div className="text-sm text-slate-400 mb-2">Decision Rights</div>
                <div className="text-white whitespace-pre-wrap">{wizard.decision_rights || "-"}</div>
              </div>
            </div>
          </details>

          {/* Step 2 */}
          <details className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl overflow-hidden">
            <summary className="px-6 py-4 cursor-pointer hover:bg-white/5 transition font-semibold">
              Proof of Funds
            </summary>
            <div className="px-6 py-4 border-t border-white/10">
              {wizard.pof_files && wizard.pof_files.length > 0 ? (
                <div className="space-y-2">
                  {wizard.pof_files.map((file, idx) => (
                    <div key={idx} className="bg-slate-800/30 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium">{file.file_name}</div>
                        {file.file_url && (
                          <a href={file.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300">
                            {file.file_url}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-slate-400">No files uploaded</div>
              )}
            </div>
          </details>

          {/* Step 3 */}
          <details className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl overflow-hidden">
            <summary className="px-6 py-4 cursor-pointer hover:bg-white/5 transition font-semibold">
              Track Record & References
            </summary>
            <div className="px-6 py-4 border-t border-white/10 space-y-4">
              <div>
                <div className="text-sm text-slate-400 mb-2">Track Record</div>
                <div className="text-white whitespace-pre-wrap">{wizard.track_record || "-"}</div>
              </div>
              <div>
                <div className="text-sm text-slate-400 mb-2">References</div>
                {wizard.references && wizard.references.length > 0 ? (
                  <div className="space-y-3">
                    {wizard.references.map((ref, idx) => (
                      <div key={idx} className="bg-slate-800/30 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-slate-400">Name:</span> <span className="text-white">{ref.name || "-"}</span></div>
                          <div><span className="text-slate-400">Role:</span> <span className="text-white">{ref.role || "-"}</span></div>
                          <div><span className="text-slate-400">Company:</span> <span className="text-white">{ref.company || "-"}</span></div>
                          <div><span className="text-slate-400">Email:</span> <span className="text-white">{ref.email || "-"}</span></div>
                        </div>
                        <div className="mt-2 text-xs">
                          <span className={`${ref.consent ? "text-green-400" : "text-red-400"}`}>
                            {ref.consent ? "✓ Consent provided" : "✗ No consent"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-slate-400">No references provided</div>
                )}
              </div>
            </div>
          </details>

          {/* Step 4 */}
          <details className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl overflow-hidden">
            <summary className="px-6 py-4 cursor-pointer hover:bg-white/5 transition font-semibold">
              Thesis & Jigsaw Fit
            </summary>
            <div className="px-6 py-4 border-t border-white/10 space-y-4">
              <div>
                <div className="text-sm text-slate-400 mb-2">Investment Thesis</div>
                <div className="text-white whitespace-pre-wrap">{wizard.thesis || "-"}</div>
              </div>
              <div>
                <div className="text-sm text-slate-400 mb-2">Jigsaw Fit</div>
                <div className="text-white whitespace-pre-wrap">{wizard.jigsaw_fit || "-"}</div>
              </div>
            </div>
          </details>

          {/* Step 5 */}
          <details className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl overflow-hidden">
            <summary className="px-6 py-4 cursor-pointer hover:bg-white/5 transition font-semibold">
              Operator Plans
            </summary>
            <div className="px-6 py-4 border-t border-white/10 space-y-4">
              <div>
                <div className="text-sm text-slate-400 mb-2">First 100 Days Plan</div>
                <div className="text-white whitespace-pre-wrap">{wizard.plan_100d || "-"}</div>
              </div>
              <div>
                <div className="text-sm text-slate-400 mb-2">12-Month Plan</div>
                <div className="text-white whitespace-pre-wrap">{wizard.plan_12m || "-"}</div>
              </div>
            </div>
          </details>

          {/* Step 6 */}
          <details className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl overflow-hidden">
            <summary className="px-6 py-4 cursor-pointer hover:bg-white/5 transition font-semibold">
              Perimeter & Structure Details
            </summary>
            <div className="px-6 py-4 border-t border-white/10 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-slate-400 mb-1">Advisors</div>
                  <div className="text-white">{structure.advisors || "-"}</div>
                </div>
              </div>
            </div>
          </details>
        </div>

        {/* Internal Notes & Decision */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8">
          <h2 className="text-xl font-bold mb-6">Internal Notes & Decision</h2>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Internal Notes (not visible to buyer)
            </label>
            <textarea
              rows={4}
              className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
              placeholder="Add internal notes for the team..."
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => handleDecision("reviewed")}
              disabled={saving}
              className="px-6 py-3 rounded-xl bg-slate-600 hover:bg-slate-700 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Mark as Reviewed
            </button>
            <button
              onClick={() => handleDecision("serious")}
              disabled={saving}
              className="px-6 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Flag as SERIOUS
            </button>
            <button
              onClick={() => handleDecision("tourist")}
              disabled={saving}
              className="px-6 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Flag as TOURIST
            </button>
          </div>

          {saving && (
            <div className="mt-4 text-sm text-cyan-400">Saving decision...</div>
          )}
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AdminSubmissionDetail;
